import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Progress - Progress bar component
 * Useful for showing completion status, loading states, etc.
 */
const Progress = ({ 
  value = 0, 
  max = 100,
  size = 'default', // 'sm', 'default', 'lg'
  variant = 'default', // 'default', 'success', 'warning', 'danger'
  showLabel = false,
  label,
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  // Ensure value is within bounds
  const normalizedValue = Math.min(Math.max(value, 0), max);
  const percentage = (normalizedValue / max) * 100;

  // Size variants
  const sizeClasses = {
    sm: 'h-2',
    default: 'h-3',
    lg: 'h-4'
  };

  // Color variants
  const getVariantClasses = () => {
    switch (variant) {
      case 'success':
        return 'bg-green-500 dark:bg-green-400';
      case 'warning':
        return 'bg-yellow-500 dark:bg-yellow-400';
      case 'danger':
        return 'bg-red-500 dark:bg-red-400';
      default:
        return 'bg-blue-500 dark:bg-blue-400';
    }
  };

  const progressElement = (
    <div
      className={`
        relative w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700
        ${sizeClasses[size] || sizeClasses.default}
        ${className}
      `}
      {...props}
    >
      {/* Progress Bar */}
      <div
        className={`
          h-full transition-all duration-500 ease-out rounded-full
          ${getVariantClasses()}
        `}
        style={{ width: `${percentage}%` }}
      />
      
      {/* Optional animated shimmer effect */}
      {percentage > 0 && percentage < 100 && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"
          style={{
            transform: `translateX(-100%)`,
            animation: 'shimmer 2s infinite'
          }}
        />
      )}
    </div>
  );

  // If no label needed, return just the progress bar
  if (!showLabel && !label) {
    return progressElement;
  }

  // Return progress bar with label
  return (
    <div className="space-y-2">
      {/* Label and percentage */}
      <div className="flex items-center justify-between">
        {label && (
          <span className={`text-sm font-medium ${theme.text.primary}`}>
            {label}
          </span>
        )}
        {showLabel && (
          <span className={`text-sm ${theme.text.secondary}`}>
            {Math.round(percentage)}%
          </span>
        )}
      </div>
      
      {/* Progress bar */}
      {progressElement}
    </div>
  );
};

/**
 * CircularProgress - Circular progress indicator
 */
const CircularProgress = ({ 
  value = 0, 
  max = 100,
  size = 48,
  strokeWidth = 4,
  variant = 'default',
  showLabel = true,
  className = '',
  ...props 
}) => {
  const { theme } = useTheme();

  const normalizedValue = Math.min(Math.max(value, 0), max);
  const percentage = (normalizedValue / max) * 100;
  
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Color variants
  const getStrokeColor = () => {
    switch (variant) {
      case 'success':
        return 'stroke-green-500 dark:stroke-green-400';
      case 'warning':
        return 'stroke-yellow-500 dark:stroke-yellow-400';
      case 'danger':
        return 'stroke-red-500 dark:stroke-red-400';
      default:
        return 'stroke-blue-500 dark:stroke-blue-400';
    }
  };

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} {...props}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-gray-200 dark:text-gray-700"
        />
        
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={`transition-all duration-500 ease-out ${getStrokeColor()}`}
        />
      </svg>
      
      {/* Center label */}
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xs font-semibold ${theme.text.primary}`}>
            {Math.round(percentage)}%
          </span>
        </div>
      )}
    </div>
  );
};

// Add shimmer animation keyframes to document if not already present
if (typeof document !== 'undefined' && !document.getElementById('progress-shimmer-styles')) {
  const style = document.createElement('style');
  style.id = 'progress-shimmer-styles';
  style.textContent = `
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `;
  document.head.appendChild(style);
}

export { Progress, CircularProgress };
export default Progress;
